import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { GoogleSignInButton } from './GoogleSignInButton';
import { Button } from '../components/ui/Button';
import { LoadingState } from '../components/ui/States';

/** Blocks the agency app until a principal is loaded; shows sign-in otherwise. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status, error, signIn, completeGoogleSignIn, googleAvailable, refresh } = useAuth();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Signing you in…" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <CenteredCard
        title="Couldn’t reach the API"
        body={error ?? 'The API may not be running. Start it and try again.'}
        action={<Button onClick={() => void refresh()}>Retry</Button>}
      />
    );
  }

  if (status === 'unauthenticated') {
    // On a Google install, Google is what the CUSTOMER'S staff use and is offered first. Microsoft
    // stays beside it, not instead of it: it is how the Alloterra administrators sign in to that
    // same install, including to configure Google in the first place. Never only Google — that
    // would lock the administrators out of an install they are standing up.
    return (
      <CenteredCard
        title="Sign in to Smorgas Social"
        body={
          googleAvailable
            ? 'Use your Google Workspace account to continue.'
            : 'Use your work account to continue.'
        }
        action={
          googleAvailable ? (
            <div className="space-y-3">
              {/* Google renders this button itself and owns the press - see GoogleSignInButton. */}
              <GoogleSignInButton onToken={(t) => void completeGoogleSignIn(t)} />
              <button
                type="button"
                onClick={() => void signIn()}
                className="focus-ring w-full rounded-lg text-sm text-ink-muted underline-offset-2 hover:text-ink hover:underline"
              >
                Administrator sign-in
              </button>
              {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
            </div>
          ) : (
            <Button onClick={() => void signIn()}>Sign in</Button>
          )
        }
      />
    );
  }

  return <>{children}</>;
}

function CenteredCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-card p-8 text-center shadow-panel">
        {/* The card follows the theme, so the logo does too: dark lettering on the light card, white
            on the dark one. The files have no size of their own, hence width and height. */}
        <img
          src="/smorgas-social-logo-light.svg"
          alt="Smorgas Social"
          width={261}
          height={40}
          className="mx-auto mb-5 block h-10 w-auto dark:hidden"
        />
        <img
          src="/smorgas-social-logo.svg"
          alt="Smorgas Social"
          width={261}
          height={40}
          className="mx-auto mb-5 hidden h-10 w-auto dark:block"
        />
        <h1 className="text-lg font-semibold text-ink">{title}</h1>
        <p className="mt-1 text-sm text-ink-muted">{body}</p>
        <div className="mt-6">{action}</div>
      </div>
    </div>
  );
}
